import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Button, AppBar, Toolbar, Container, Link } from '@mui/material';
// hooks
import useOffSetTop from '../../hooks/minimal/useOffSetTop';
import useResponsive from '../../hooks/minimal/useResponsive';
// utils
import { bgBlur } from '../../utils/minimal/cssStyles';
// config
import { HEADER } from '../../config-global';
// routes
import { PATH_DASHBOARD, PATH_DOCS } from '../../routes/paths';
// components
import Logo from '../../components/minimal/logo';
import Label from '../../components/minimal/label';
//
import NavMobile from './nav/mobile';
import NavDesktop from './nav/desktop';
import { useLocales } from "../../locales";
import { Link as RouterLink } from "react-router-dom";
import { getMarketplaceLogo, isNimi, getMarketPlaceName } from "../../utils/market";
import { varFade, MotionContainer } from '../../components/minimal/animate';
import { m } from 'framer-motion';
import Iconify from '../../components/minimal/iconify';
import { useSettingsContext } from '../../components/minimal/settings';
// ----------------------------------------------------------------------
import { Stack } from '@mui/system';
import { default as DeskTopNavList } from './nav/desktop/NavList';
import { useParams, useLocation } from 'react-router-dom';
import { useSellerPageContext } from '../../context-state/SellerPageContext';
import { useEventContext } from '../../context-state/EventContext';
import { isEventPage } from '../../context-state/AuthContext';
import * as API from '../../api';
import SellerJumbotron from '../../pages/seller-public/SellerJumbotron';
import { getEndUserTokenKeyByHandler } from '../../utils/endUser';
import { useState } from 'react';
import _ from 'lodash';
import { fetchEndUserData } from '../../services/endUserService';

export const SellerPublicPageHeader = () => {
    const { handler } = useParams();
    const { endUserContext, accountContext } = useSellerPageContext();
    const { endUser } = endUserContext;
    const { account } = accountContext;

    const hideSubscriptions = account?.marketPlace?.subscription_are_manually_approved &&
        !endUser?.approved_for_subscription_purchase;

    return (<>
        <EndUserHeader
            isAllowSeasonTicket={account?.isAllowSeasonTicket}
            sellerHandler={handler}
            endUser={endUser}
            hideLoginLink={account?.design?.sellerPageHideLoginLink}
            hideSubscriptionLink={account?.design?.sellerPageHideSubscriptionLink || hideSubscriptions}
            hideEntryPassLink={account?.design?.sellerPageHideEntryPassLink}
            logo={account?.design?.profileImageIsLogo && account?.design?.profileImage ? account?.design?.profileImage : null}
        />
        <SellerJumbotron />
    </>);
}

export const EventPageHeader = () => {
    const { eventPageContext } = useEventContext();
    const { endUser, setEndUser, account } = eventPageContext;
    const isSeller = account.isSubAccount === false;
    const [seller, setSeller] = useState(isSeller ? account : account?.parent);

    const hideSubscriptions = account?.marketPlace?.subscription_are_manually_approved &&
        !endUser?.approved_for_subscription_purchase;
    const fetchData = async () => {
        const result = await fetchEndUserData(seller, account);
        if (result) {
            setSeller(result.seller);
            // Include hasActiveSubscription to prevent losing subscription status
            setEndUser({...result.endUser, hasActiveSubscription: result.hasActiveSubscription});
            eventPageContext.setInitialUserEventDetails(result.endUser);
        }
    }

    useEffect(() => {
        fetchData();
    }, [])

    if (!seller?.handler) {
        return null; // there in no seller handler so nothing to show
    }

    return (<EndUserHeader
        isAllowSeasonTicket={account?.isAllowSeasonTicket}
        sellerHandler={seller?.handler}
        endUser={endUser}
        hideLoginLink={seller?.design?.sellerPageHideLoginLink}
        hideSubscriptionLink={seller?.design?.sellerPageHideSubscriptionLink || hideSubscriptions}
        hideEntryPassLink={seller?.design?.sellerPageHideEntryPassLink}
        logo={seller.design.profileImageIsLogo && seller.design.profileImage ? seller.design.profileImage : null}
    />);
}


export default function EndUserHeader({ sellerHandler, endUser, isAllowSeasonTicket, hideLoginLink, hideSubscriptionLink, hideEntryPassLink, logo }) {
    const carouselRef = useRef(null);
    const { translate } = useLocales();
    const theme = useTheme();
    const location = useLocation();

    const navConfig = [
        {
            title: 'events',
            path: `/seller/${sellerHandler}/events`,
        },
    ];

    if (isAllowSeasonTicket && hideSubscriptionLink !== true) {
        navConfig.push({
            title: 'subscriptions',
            path: `/seller/${sellerHandler}/subscriptions`,
        });
    }

    if (isAllowSeasonTicket && hideEntryPassLink !== true) {
        navConfig.push({
            title: 'entry_pass',
            path: `/seller/${sellerHandler}/entry-pass`,
        });
    }

    if (!endUser && hideLoginLink !== true) {
        // If on event page, capture the current URL as returnUrl
        let signInPath = `/seller/${sellerHandler}/signin`;
        if (isEventPage()) {
            const returnUrl = encodeURIComponent(location.pathname + location.search);
            signInPath = `${signInPath}?returnUrl=${returnUrl}`;
        }
        navConfig.push({
            title: 'signin',
            path: signInPath,
        });
    } else if (endUser) {
        navConfig.push({
            title: 'my_account',
            path: `/seller/${sellerHandler}/my-account`,
        });
    }

    const isDesktop = useResponsive('up', 'md');
    const { themeDirection } = useSettingsContext();
    const isOffset = useOffSetTop(HEADER.H_MAIN_DESKTOP);
    const isOffsetScreenHeight = useOffSetTop(600);
    const currentMarketplaceLogo = getMarketplaceLogo();
    let logoComponent;

    if (logo) {
        logoComponent = <img src={logo} style={{ width: "5rem", height: "5rem" }} alt="" />
    } else if (currentMarketplaceLogo) {
        logoComponent = <img src={currentMarketplaceLogo.path} style={{ width: currentMarketplaceLogo.width, height: currentMarketplaceLogo.height }} alt="" />
    } else {
        logoComponent = <Logo />
    }

    return (
        <AppBar ref={carouselRef} color="transparent" sx={{ boxShadow: 0 }}>
            <Toolbar
                disableGutters
                sx={{
                    height: {
                        xs: HEADER.H_MOBILE,
                        md: HEADER.H_MAIN_DESKTOP,
                    },
                    transition: theme.transitions.create(['height', 'background-color'], {
                        easing: theme.transitions.easing.easeInOut,
                        duration: theme.transitions.duration.standard,
                    }),
                    ...(isOffset && {
                        ...bgBlur({ color: theme.palette.background.default }),
                        height: {
                            md: HEADER.H_MAIN_DESKTOP - 16,
                        },
                    }),
                }}
            >
                <Container sx={{ height: 1, display: 'flex', alignItems: 'center' }}>
                    {logoComponent}

                    <Box sx={{ flexGrow: 1 }} />

                    {isDesktop && <EndUserDesktopNav isOffset={isOffset} data={navConfig} />}

                    {!isDesktop && <NavMobile isOffset={isOffset} data={navConfig} logoComponent={logoComponent} />}
                </Container>
            </Toolbar>

        </AppBar>
    );
}

// ----------------------------------------------------------------------

Shadow.propTypes = {
    sx: PropTypes.object,
};

function Shadow({ sx, ...other }) {
    return (
        <Box
            sx={{
                left: 0,
                right: 0,
                bottom: 0,
                height: 24,
                zIndex: -1,
                m: 'auto',
                borderRadius: '50%',
                position: 'absolute',
                width: `calc(100% - 48px)`,
                boxShadow: (theme) => theme.customShadows.z8,
                ...sx,
            }}
            {...other}
        />
    );
}

const EndUserDesktopNav = ({ isOffset, data }) => {
    return (
        <Stack component="nav" direction="row" gap={2} sx={{ height: 1 }}>
            {data.map((link) => (
                <DeskTopNavList key={link.title} item={link} isOffset={isOffset} />
            ))}
        </Stack>
    );
}